import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { ApiError, api } from './api/client';
import type { User } from './types';

export type ProfilePayload = {
  name?: string;
  email?: string;
  about_me?: string | null;
  current_password?: string;
  password?: string;
  password_confirmation?: string;
};

export type ReviewCounts = { users: number; submissions: number };

type AuthState = {
  user: User | null;
  loading: boolean;
  logout: () => Promise<void>;
  updateProfile: (payload: ProfilePayload) => Promise<User>;
  refresh: () => Promise<void>;
  reviewCounts: ReviewCounts;
  refreshReviewCounts: () => Promise<void>;
};

const AuthCtx = createContext<AuthState | null>(null);

const AUTH_ORIGIN = 'https://auth.internetsociology.org';

/** Send the browser to the central login app, asking it to bounce
 *  back to where the user was when this fired. */
export function redirectToLogin(): void {
  const returnTo = encodeURIComponent(window.location.href);
  window.location.href = `${AUTH_ORIGIN}/login?return_to=${returnTo}`;
}

const REVIEW_COUNTS_POLL_MS = 60_000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [reviewCounts, setReviewCounts] = useState<ReviewCounts>({ users: 0, submissions: 0 });

  useEffect(() => {
    api<{ data: User }>('/auth/me')
      .then(({ data }) => setUser(data))
      .catch((e) => {
        if (!(e instanceof ApiError && e.status === 401)) {
          console.error('auth/me failed', e);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  // Admins poll the review-counts endpoint so the navbar badges stay
  // roughly fresh even without page navigation. Mutating actions
  // (approve/reject) call refreshReviewCounts() for immediate feedback;
  // this poll is the safety net.
  useEffect(() => {
    if (user?.role !== 'admin') {
      setReviewCounts({ users: 0, submissions: 0 });
      return;
    }
    let cancelled = false;
    async function load() {
      try {
        const { data } = await api<{ data: ReviewCounts }>('/admin/review-counts');
        if (!cancelled) setReviewCounts(data);
      } catch {
        // Best-effort; badge just stays at its previous value on transient failures.
      }
    }
    load();
    const id = window.setInterval(load, REVIEW_COUNTS_POLL_MS);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [user?.role]);

  async function refreshReviewCounts() {
    if (user?.role !== 'admin') return;
    try {
      const { data } = await api<{ data: ReviewCounts }>('/admin/review-counts');
      setReviewCounts(data);
    } catch { /* best-effort */ }
  }

  async function logout() {
    try { await api('/auth/logout', { method: 'POST' }); } catch { /* ignore */ }
    // Sweep the user back to the apex marketing site rather than leaving
    // them on a now-unauthenticated page that would just bounce them to
    // the auth app to log back in.
    window.location.href = 'https://internetsociology.org/';
  }

  async function updateProfile(payload: ProfilePayload): Promise<User> {
    const { data } = await api<{ data: User }>('/auth/profile', {
      method: 'POST',
      body: payload,
    });
    setUser(data);
    return data;
  }

  async function refresh() {
    try {
      const { data } = await api<{ data: User }>('/auth/me');
      setUser(data);
    } catch {
      setUser(null);
    }
  }

  return (
    <AuthCtx.Provider value={{ user, loading, logout, updateProfile, refresh, reviewCounts, refreshReviewCounts }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
